import type { FieldDef } from './fieldTypes'

export const EXAMPLE_FIELDS: Record<string, FieldDef[]> = {
  'button-hover': [
    { type: 'slider', id: 'hoverScale', label: 'Hover Scale', group: 'Animation', min: 1, max: 1.5, step: 0.01, defaultValue: 1.05 },
    { type: 'slider', id: 'tapScale', label: 'Tap Scale', group: 'Animation', min: 0.8, max: 1, step: 0.01, defaultValue: 0.97 },
    { type: 'slider', id: 'stiffness', label: 'Stiffness', group: 'Spring', min: 50, max: 1000, step: 10, defaultValue: 400 },
    { type: 'slider', id: 'damping', label: 'Damping', group: 'Spring', min: 1, max: 50, step: 1, defaultValue: 17 },
    { type: 'color', id: 'bgColor', label: 'Button Color', group: 'Style', defaultValue: 'bg-indigo-600' },
  ],

  'toggle-switch': [
    { type: 'slider', id: 'stiffness', label: 'Stiffness', group: 'Spring', min: 100, max: 1000, step: 10, defaultValue: 500 },
    { type: 'slider', id: 'damping', label: 'Damping', group: 'Spring', min: 5, max: 60, step: 1, defaultValue: 30 },
    { type: 'color', id: 'onColor', label: 'On Color', group: 'Colors', defaultValue: 'bg-green-500' },
    { type: 'color', id: 'offColor', label: 'Off Color', group: 'Colors', defaultValue: 'bg-gray-300' },
    { type: 'slider', id: 'knobSize', label: 'Knob Size', group: 'Dimensions', min: 20, max: 36, step: 1, unit: 'px', defaultValue: 28 },
  ],

  'slide-fade': [
    { type: 'slider', id: 'initialX', label: 'Initial X', group: 'Animation', min: -120, max: 0, step: 5, unit: 'px', defaultValue: -60 },
    { type: 'slider', id: 'initialOpacity', label: 'Initial Opacity', group: 'Animation', min: 0, max: 1, step: 0.05, defaultValue: 0 },
    { type: 'slider', id: 'stiffness', label: 'Stiffness', group: 'Spring', min: 50, max: 600, step: 10, defaultValue: 260 },
    { type: 'slider', id: 'damping', label: 'Damping', group: 'Spring', min: 5, max: 40, step: 1, defaultValue: 20 },
  ],

  'photo-flip': [
    { type: 'slider', id: 'duration', label: 'Flip Duration', group: 'Animation', min: 0.2, max: 2, step: 0.1, unit: 's', defaultValue: 0.6 },
    { type: 'slider', id: 'perspective', label: 'Perspective', group: 'Animation', min: 300, max: 1500, step: 50, unit: 'px', defaultValue: 800 },
    { type: 'toggle', id: 'useSpring', label: 'Use Spring', group: 'Animation', defaultValue: false },
    { type: 'slider', id: 'stiffness', label: 'Stiffness', group: 'Spring', min: 50, max: 500, step: 10, defaultValue: 200 },
    { type: 'slider', id: 'damping', label: 'Damping', group: 'Spring', min: 5, max: 50, step: 1, defaultValue: 25 },
  ],

  'crossfade-scale': [
    { type: 'slider', id: 'initialScale', label: 'Enter Scale', group: 'Animation', min: 0.5, max: 1, step: 0.05, defaultValue: 0.9 },
    { type: 'slider', id: 'exitScale', label: 'Exit Scale', group: 'Animation', min: 1, max: 1.5, step: 0.05, defaultValue: 1.05 },
    { type: 'slider', id: 'duration', label: 'Duration', group: 'Transition', min: 0.1, max: 1.5, step: 0.05, unit: 's', defaultValue: 0.4 },
    {
      type: 'select', id: 'ease', label: 'Easing', group: 'Transition', defaultValue: 'easeInOut',
      options: [
        { label: 'Ease In Out', value: 'easeInOut' },
        { label: 'Ease In', value: 'easeIn' },
        { label: 'Ease Out', value: 'easeOut' },
        { label: 'Linear', value: 'linear' },
      ],
    },
  ],

  'reveal-on-scroll': [
    { type: 'slider', id: 'initialY', label: 'Initial Y', group: 'Animation', min: 10, max: 80, step: 5, unit: 'px', defaultValue: 30 },
    { type: 'slider', id: 'initialOpacity', label: 'Initial Opacity', group: 'Animation', min: 0, max: 1, step: 0.05, defaultValue: 0 },
    { type: 'slider', id: 'duration', label: 'Duration', group: 'Transition', min: 0.1, max: 1.5, step: 0.05, unit: 's', defaultValue: 0.5 },
    { type: 'slider', id: 'staggerDelay', label: 'Stagger Delay', group: 'Transition', min: 0, max: 0.3, step: 0.01, unit: 's', defaultValue: 0.1 },
    { type: 'slider', id: 'itemCount', label: 'Item Count', group: 'Layout', min: 3, max: 12, step: 1, defaultValue: 8 },
  ],

  'parallax-layers': [
    { type: 'slider', id: 'depth1', label: 'Layer 1 Depth', group: 'Parallax', min: 5, max: 60, step: 1, unit: 'px', defaultValue: 15 },
    { type: 'slider', id: 'depth2', label: 'Layer 2 Depth', group: 'Parallax', min: 10, max: 80, step: 1, unit: 'px', defaultValue: 30 },
    { type: 'slider', id: 'depth3', label: 'Layer 3 Depth', group: 'Parallax', min: 15, max: 120, step: 1, unit: 'px', defaultValue: 50 },
    { type: 'slider', id: 'smoothing', label: 'Smoothing', group: 'Spring', min: 30, max: 300, step: 10, defaultValue: 100 },
  ],

  'skeleton-shimmer': [
    { type: 'slider', id: 'duration', label: 'Shimmer Speed', group: 'Animation', min: 0.5, max: 4, step: 0.1, unit: 's', defaultValue: 1.5 },
    { type: 'slider', id: 'delay1', label: 'Row 1 Delay', group: 'Stagger', min: 0, max: 1, step: 0.05, unit: 's', defaultValue: 0.2 },
    { type: 'slider', id: 'delay2', label: 'Row 2 Delay', group: 'Stagger', min: 0, max: 1, step: 0.05, unit: 's', defaultValue: 0.4 },
    { type: 'slider', id: 'delay3', label: 'Row 3 Delay', group: 'Stagger', min: 0, max: 1, step: 0.05, unit: 's', defaultValue: 0.6 },
  ],

  'success-checkmark': [
    { type: 'slider', id: 'circleDuration', label: 'Circle Duration', group: 'Animation', min: 0.2, max: 2, step: 0.1, unit: 's', defaultValue: 0.6 },
    { type: 'slider', id: 'checkDelay', label: 'Check Delay', group: 'Animation', min: 0, max: 1.5, step: 0.05, unit: 's', defaultValue: 0.5 },
    { type: 'slider', id: 'stiffness', label: 'Check Stiffness', group: 'Spring', min: 50, max: 500, step: 10, defaultValue: 200 },
    { type: 'slider', id: 'damping', label: 'Check Damping', group: 'Spring', min: 5, max: 40, step: 1, defaultValue: 15 },
    { type: 'color', id: 'color', label: 'Color', group: 'Style', defaultValue: 'bg-green-500' },
    { type: 'slider', id: 'strokeWidth', label: 'Stroke Width', group: 'Style', min: 1, max: 6, step: 0.5, unit: 'px', defaultValue: 3 },
  ],

  'expandable-card': [
    { type: 'slider', id: 'stiffness', label: 'Stiffness', group: 'Spring', min: 100, max: 600, step: 10, defaultValue: 300 },
    { type: 'slider', id: 'damping', label: 'Damping', group: 'Spring', min: 10, max: 50, step: 1, defaultValue: 30 },
    { type: 'slider', id: 'contentDuration', label: 'Content Duration', group: 'Animation', min: 0.1, max: 1, step: 0.05, unit: 's', defaultValue: 0.3 },
  ],

  'tab-transition': [
    { type: 'slider', id: 'stiffness', label: 'Tab Stiffness', group: 'Spring', min: 100, max: 800, step: 10, defaultValue: 400 },
    { type: 'slider', id: 'damping', label: 'Tab Damping', group: 'Spring', min: 10, max: 50, step: 1, defaultValue: 30 },
    { type: 'slider', id: 'fadeDuration', label: 'Content Fade', group: 'Animation', min: 0.05, max: 0.8, step: 0.05, unit: 's', defaultValue: 0.2 },
    { type: 'slider', id: 'yOffset', label: 'Y Offset', group: 'Animation', min: 0, max: 30, step: 1, unit: 'px', defaultValue: 8 },
  ],

  'cascading-grid': [
    { type: 'slider', id: 'staggerDelay', label: 'Stagger Delay', group: 'Animation', min: 0.01, max: 0.2, step: 0.01, unit: 's', defaultValue: 0.06 },
    { type: 'slider', id: 'initialScale', label: 'Initial Scale', group: 'Animation', min: 0.3, max: 1, step: 0.05, defaultValue: 0.8 },
    { type: 'slider', id: 'initialY', label: 'Initial Y', group: 'Animation', min: 0, max: 40, step: 2, unit: 'px', defaultValue: 10 },
    { type: 'slider', id: 'columns', label: 'Columns', group: 'Layout', min: 2, max: 6, step: 1, defaultValue: 4 },
    { type: 'slider', id: 'gap', label: 'Gap', group: 'Layout', min: 4, max: 16, step: 1, unit: 'px', defaultValue: 8 },
  ],

  'text-letter-by-letter': [
    { type: 'text', id: 'text', label: 'Text', group: 'Content', defaultValue: 'Nimble Labs' },
    { type: 'slider', id: 'staggerDelay', label: 'Stagger Delay', group: 'Animation', min: 0.01, max: 0.15, step: 0.005, unit: 's', defaultValue: 0.05 },
    { type: 'slider', id: 'stiffness', label: 'Stiffness', group: 'Spring', min: 100, max: 600, step: 10, defaultValue: 300 },
    { type: 'slider', id: 'damping', label: 'Damping', group: 'Spring', min: 5, max: 40, step: 1, defaultValue: 20 },
    { type: 'slider', id: 'initialY', label: 'Initial Y', group: 'Animation', min: 5, max: 50, step: 1, unit: 'px', defaultValue: 20 },
  ],

  'line-drawing': [
    { type: 'color', id: 'strokeColor', label: 'Triangle Color', group: 'Colors', defaultValue: 'bg-indigo-500' },
    { type: 'slider', id: 'strokeWidth', label: 'Triangle Stroke', group: 'Style', min: 1, max: 6, step: 0.5, unit: 'px', defaultValue: 2.5 },
    { type: 'slider', id: 'duration', label: 'Draw Duration', group: 'Animation', min: 0.5, max: 5, step: 0.1, unit: 's', defaultValue: 2 },
    { type: 'color', id: 'circleColor', label: 'Circle Color', group: 'Colors', defaultValue: 'bg-purple-500' },
    { type: 'slider', id: 'circleDelay', label: 'Circle Delay', group: 'Animation', min: 0, max: 2, step: 0.1, unit: 's', defaultValue: 0.8 },
  ],

  'shape-morph': [
    { type: 'slider', id: 'rotate', label: 'Rotation', group: 'Animation', min: 0, max: 360, step: 5, unit: 'deg', defaultValue: 180 },
    { type: 'slider', id: 'scale', label: 'Scale', group: 'Animation', min: 0.5, max: 2, step: 0.05, defaultValue: 1.1 },
    { type: 'slider', id: 'stiffness', label: 'Stiffness', group: 'Spring', min: 50, max: 500, step: 10, defaultValue: 200 },
    { type: 'slider', id: 'damping', label: 'Damping', group: 'Spring', min: 5, max: 40, step: 1, defaultValue: 20 },
    { type: 'slider', id: 'borderRadiusFrom', label: 'Radius (Square)', group: 'Style', min: 0, max: 50, step: 1, unit: '%', defaultValue: 12 },
    { type: 'slider', id: 'borderRadiusTo', label: 'Radius (Circle)', group: 'Style', min: 0, max: 50, step: 1, unit: '%', defaultValue: 50 },
  ],

  'draggable-spring-back': [
    { type: 'slider', id: 'dragElastic', label: 'Elasticity', group: 'Drag', min: 0, max: 1, step: 0.05, defaultValue: 0.6 },
    { type: 'slider', id: 'dragScale', label: 'Drag Scale', group: 'Drag', min: 1, max: 1.5, step: 0.05, defaultValue: 1.1 },
    { type: 'slider', id: 'stiffness', label: 'Stiffness', group: 'Spring', min: 50, max: 800, step: 10, defaultValue: 300 },
    { type: 'slider', id: 'damping', label: 'Damping', group: 'Spring', min: 5, max: 40, step: 1, defaultValue: 20 },
  ],

  'swipe-to-dismiss': [
    { type: 'slider', id: 'swipeThreshold', label: 'Swipe Threshold', group: 'Drag', min: 30, max: 200, step: 10, unit: 'px', defaultValue: 80 },
    { type: 'slider', id: 'initialX', label: 'Enter X', group: 'Animation', min: -80, max: 0, step: 5, unit: 'px', defaultValue: -40 },
    { type: 'slider', id: 'exitX', label: 'Exit X', group: 'Animation', min: 100, max: 400, step: 10, unit: 'px', defaultValue: 200 },
    { type: 'slider', id: 'dragScale', label: 'Drag Scale', group: 'Drag', min: 1, max: 1.2, step: 0.01, defaultValue: 1.02 },
  ],
}
